import axios from 'axios';

const API_BASE_URL = '/api/v1/auth';
const EMAIL_AUTH_BASE_URL = '/api/v1/email-auth';

/**
 * Auth Service
 * Handles all authentication-related API calls
 */

// Sign up a new user
export const signup = async (username, email, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/signup`, {
      username,
      email,
      password
    });
    return {
      success: true,
      message: response.data.message
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'Signup failed. Please try again.'
    };
  }
};

// Login user and get JWT token
export const login = async (username, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/login`, {
      username,
      password
    });

    const { access_token, token_type, user } = response.data;

    // Store token in localStorage (or you can use sessionStorage)
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('token_type', token_type);
    localStorage.setItem('user', JSON.stringify(user));

    return {
      success: true,
      token: access_token,
      user: user
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'Login failed. Please check your credentials.'
    };
  }
};

// Logout user
export const logout = async () => {
  try {
    await axios.post(`${API_BASE_URL}/logout`);

    // Clear stored token and user data
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user');

    return {
      success: true,
      message: 'Logged out successfully'
    };
  } catch (error) {
    // Even if API call fails, clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user');

    return {
      success: true,
      message: 'Logged out'
    };
  }
};

// Get current user from localStorage
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    return null;
  }
};

// Get token from localStorage
export const getToken = () => {
  return localStorage.getItem('access_token');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getToken();
};

// ============ Email Verification Auth ============

// Send verification code to email (auto-registers new users)
export const sendVerificationCode = async (email) => {
  try {
    const response = await axios.post(`${EMAIL_AUTH_BASE_URL}/send-code`, {
      email
    });
    return {
      success: true,
      message: response.data.message,
      isNewUser: response.data.is_new_user,
      expiresIn: response.data.expires_in
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'Failed to send verification code. Please try again.'
    };
  }
};

// Verify code and get JWT token
export const verifyCode = async (email, code) => {
  try {
    const response = await axios.post(`${EMAIL_AUTH_BASE_URL}/verify`, {
      email,
      code
    });

    const { access_token, token_type, user } = response.data;

    // Store token in localStorage
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('token_type', token_type);
    localStorage.setItem('user', JSON.stringify(user));

    return {
      success: true,
      token: access_token,
      user: user
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'Verification failed. Please check your code.'
    };
  }
};

// Google OAuth login using ID token
export const loginWithGoogle = async (credential) => {
  try {
    const response = await axios.post(`${EMAIL_AUTH_BASE_URL}/google`, {
      credential
    });

    const { access_token, token_type, user } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('token_type', token_type);
    localStorage.setItem('user', JSON.stringify(user));

    return {
      success: true,
      token: access_token,
      user: user
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'Google login failed. Please try again.'
    };
  }
};

// Update username (email auth)
/**
 * Save which preset profile picture the user picked.
 *
 * The images are ours; the backend only stores the index, 1..16. See
 * components/AccountPage/avatars.js for the mapping — renumbering it silently
 * changes the picture of everyone who already chose.
 */
export const updateAvatar = async (avatarId) => {
  try {
    const response = await axios.put(`${EMAIL_AUTH_BASE_URL}/avatar`, {
      avatar_id: avatarId
    });

    const { user, message } = response.data;
    /* Some deployments return only {message} from this endpoint. Keep the
       authenticated user and the sidebar in sync immediately instead of
       waiting for a later /me refresh. */
    const currentUser = getCurrentUser();
    const nextUser = user || (currentUser ? { ...currentUser, avatar_id: avatarId } : null);
    if (nextUser) {
      localStorage.setItem('user', JSON.stringify(nextUser));
    }

    return {
      success: true,
      message: message || 'Avatar updated successfully',
      user: nextUser
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      message: error.response?.data?.detail || 'Failed to update avatar. Please try again.'
    };
  }
};

/**
 * The authoritative read of the current user. A picture chosen on one device is
 * invisible to another until that device re-reads, and the cached login payload
 * predates the choice.
 */
export const fetchCurrentUser = async () => {
  try {
    const response = await axios.get(`${EMAIL_AUTH_BASE_URL}/me`);
    if (response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return { success: true, user: response.data };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      message: error.response?.data?.detail || 'Failed to load your account.'
    };
  }
};

export const updateUsername = async (newUsername) => {
  try {
    const response = await axios.put(`${EMAIL_AUTH_BASE_URL}/username`, {
      new_username: newUsername
    });

    const { user, message } = response.data;
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    return {
      success: true,
      message: message || 'Username updated successfully',
      user: user
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'Failed to update username. Please try again.'
    };
  }
};
